import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface CompareToggleResult {
    added: boolean;
    removed: boolean;
    limitReached: boolean;
}

@Injectable({ providedIn: 'root' })
export class CompareService {
    static readonly MAX = 2;
    private readonly STORAGE_KEY = 'compare.productIds';

    private readonly subject = new BehaviorSubject<string[]>(this.load());

    readonly compareIds$: Observable<string[]> = this.subject.asObservable();
    readonly count$: Observable<number> = this.compareIds$.pipe(map(ids => ids.length));

    has(productId: string): boolean {
        return this.subject.value.includes(productId);
    }

    get count(): number {
        return this.subject.value.length;
    }

    get ids(): string[] {
        return [...this.subject.value];
    }

    toggle(productId: string): CompareToggleResult {
        if (!productId) {
            return { added: false, removed: false, limitReached: false };
        }
        const current = this.subject.value;
        if (current.includes(productId)) {
            const next = current.filter(id => id !== productId);
            this.update(next);
            return { added: false, removed: true, limitReached: false };
        }
        if (current.length >= CompareService.MAX) {
            return { added: false, removed: false, limitReached: true };
        }
        this.update([...current, productId]);
        return { added: true, removed: false, limitReached: false };
    }

    remove(productId: string): void {
        const next = this.subject.value.filter(id => id !== productId);
        if (next.length !== this.subject.value.length) {
            this.update(next);
        }
    }

    clear(): void {
        this.update([]);
    }

    private update(ids: string[]): void {
        this.subject.next(ids);
        if (typeof window !== 'undefined') {
            try {
                window.localStorage.setItem(this.STORAGE_KEY, JSON.stringify(ids));
            } catch {}
        }
    }

    private load(): string[] {
        if (typeof window === 'undefined') return [];
        try {
            const raw = window.localStorage.getItem(this.STORAGE_KEY);
            if (!raw) return [];
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed.filter(id => typeof id === 'string').slice(0, CompareService.MAX) : [];
        } catch {
            return [];
        }
    }
}
